import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import {
  PermissionCategoryGroupDto,
  PermissionItemDto,
} from 'http-client/types/role.types';
import {
  usePermissionsCatalogQuery,
  useRoleDetailQuery,
  useUpdateRolePermissionsMutation,
} from 'http-client/hooks/useRoleHooks';

interface RolePermissionsEditorProps {
  roleId: string;
  roleName: string;
  canEdit: boolean;
}

const formatPermissionLabel = (permission: PermissionItemDto) =>
  permission.description ||
  permission.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const RolePermissionsEditor: React.FC<RolePermissionsEditorProps> = ({
  roleId,
  roleName,
  canEdit,
}) => {
  const { data: catalog, isLoading: loadingCatalog } = usePermissionsCatalogQuery();
  const { data: roleDetail, isLoading: loadingRole } = useRoleDetailQuery(roleId);
  const { mutate: savePermissions, isPending: saving } = useUpdateRolePermissionsMutation();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const categoryCheckboxRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (roleDetail?.permissionIds) {
      setSelectedIds(new Set(roleDetail.permissionIds));
    }
  }, [roleDetail?.permissionIds]);

  const categories = useMemo(() => catalog ?? [], [catalog]);

  const getCategoryState = useCallback(
    (category: PermissionCategoryGroupDto) => {
      const ids = category.permissions.map((p) => p.id);
      if (ids.length === 0) return { checked: false, indeterminate: false };
      const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
      return {
        checked: selectedCount === ids.length,
        indeterminate: selectedCount > 0 && selectedCount < ids.length,
      };
    },
    [selectedIds],
  );

  useEffect(() => {
    categories.forEach((category) => {
      const el = categoryCheckboxRefs.current[category.key];
      if (!el) return;
      const { checked, indeterminate } = getCategoryState(category);
      el.checked = checked;
      el.indeterminate = indeterminate;
    });
  }, [categories, getCategoryState]);

  const toggleCategory = (category: PermissionCategoryGroupDto, selectAll: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      category.permissions.forEach((p) => {
        if (selectAll) next.add(p.id);
        else next.delete(p.id);
      });
      return next;
    });
  };

  const togglePermission = (permissionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  };

  const handleSave = () => {
    savePermissions({
      id: roleId,
      body: { permissionIds: Array.from(selectedIds) },
    });
  };

  if (loadingCatalog || loadingRole) {
    return (
      <div className="py-5 text-center">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 className="mb-0">{roleName}</h5>
          <small className="text-muted">
            {selectedIds.size} permission{selectedIds.size === 1 ? '' : 's'} selected
          </small>
        </div>
        {canEdit && (
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Permissions'}
          </Button>
        )}
      </CardHeader>
      <CardBody>
        {!canEdit && (
          <Alert color="warning" className="mb-4">
            Permissions for this system role cannot be modified.
          </Alert>
        )}

        <Row>
          {categories.map((category) => {
            const { checked } = getCategoryState(category);

            return (
              <Col key={category.key} lg={6} className="mb-4">
                <Card className="h-100 border">
                  <CardHeader className="bg-light py-2">
                    <FormGroup check className="mb-0">
                      <Input
                        type="checkbox"
                        id={`category-${category.key}`}
                        innerRef={(el: HTMLInputElement | null) => {
                          categoryCheckboxRefs.current[category.key] = el;
                        }}
                        checked={checked}
                        disabled={!canEdit}
                        onChange={(e) => toggleCategory(category, e.target.checked)}
                      />
                      <Label check for={`category-${category.key}`} className="fw-bold mb-0">
                        {category.displayName}
                      </Label>
                    </FormGroup>
                  </CardHeader>
                  <CardBody className="pt-2">
                    {category.permissions.map((permission) => (
                      <FormGroup check key={permission.id} className="mb-2">
                        <Input
                          type="checkbox"
                          id={`perm-${permission.id}`}
                          checked={selectedIds.has(permission.id)}
                          disabled={!canEdit}
                          onChange={() => togglePermission(permission.id)}
                        />
                        <Label check for={`perm-${permission.id}`} className="mb-0">
                          <span className="d-block">{formatPermissionLabel(permission)}</span>
                          <small className="text-muted">{permission.key}</small>
                        </Label>
                      </FormGroup>
                    ))}
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      </CardBody>
    </Card>
  );
};

export default RolePermissionsEditor;
